// The metric catalog behind the report's Summary section.
//
// The section deliberately leads with *calculated* metrics — rates, per-post
// averages and cost efficiency. The raw totals (impressions, views,
// engagements, revenue) are usually on the report already: ReportCharts
// renders each charted one as the number in the middle of its donut. Which
// ones are charted is a per-report choice (reportChartCatalog.mjs), so the
// totals stay in the picker, grouped last, and the panel marks only the ones
// this report actually charts.
//
// Everything here is a pure function of a totals object so the section tracks
// the same in-page filters the charts do, with no extra round trip.

import { abbreviateNumber } from "./abbreviateNumber.mjs";

// engagements / impressions, as a percentage.
//
// This is the house formula, not a new one: Assignment#engagement_rate,
// PartnerCampaign#total_engagement_rate, reports/benchmarks.html.haml and
// QueryReportDataTool all divide summed engagements by summed impressions.
// Note it is a weighted rate over the whole report, NOT the mean of each
// deliverable's own rate — a 2M-impression post and a 400-impression story do
// not get an equal vote.
const RATE = (numerator, denominator) => (totals) => {
  const denom = totals[denominator];
  if (!denom) return null;
  return (totals[numerator] / denom) * 100;
};

const PER_POST = (metric) => (totals) => {
  if (!totals.post_count) return null;
  return totals[metric] / totals.post_count;
};

// cost / (units / 1000) for CPM, cost / units for the rest. `cost` is 0 when
// the owner has not told us what the campaign cost, which is why every cost
// metric guards on it — dividing by a denominator we don't have would render
// a confident "$0.00".
const COST_PER = (metric, per = 1) => (totals, { cost } = {}) => {
  if (!cost || !totals[metric]) return null;
  return cost / (totals[metric] / per);
};

export const METRIC_GROUPS = [
  { key: "rates", label: "Rates" },
  { key: "averages", label: "Per post" },
  // The cost group's hint is replaced at render time with live provenance and
  // rate coverage; this is the fallback.
  { key: "cost", label: "Cost efficiency", hint: "From creator rates" },
  { key: "totals", label: "Totals" },
];

export const METRIC_DEFINITIONS = [
  // --- Rates -------------------------------------------------------------
  {
    key: "engagement_rate",
    label: "Engagement rate",
    group: "rates",
    format: "percent",
    hint: "Engagements ÷ impressions",
    compute: RATE("engagements", "impressions"),
  },
  {
    key: "view_rate",
    label: "View rate",
    group: "rates",
    format: "percent",
    hint: "Views ÷ impressions",
    compute: RATE("views", "impressions"),
  },
  {
    key: "click_through_rate",
    label: "Click-through rate",
    group: "rates",
    format: "percent",
    hint: "Link clicks ÷ impressions",
    compute: RATE("outbound_clicks", "impressions"),
  },

  // --- Per post ----------------------------------------------------------
  {
    key: "impressions_per_post",
    label: "Impressions per post",
    group: "averages",
    format: "number",
    hint: "Impressions ÷ posts",
    compute: PER_POST("impressions"),
  },
  {
    key: "engagements_per_post",
    label: "Engagements per post",
    group: "averages",
    format: "number",
    hint: "Engagements ÷ posts",
    compute: PER_POST("engagements"),
  },

  // --- Cost efficiency ---------------------------------------------------
  {
    key: "cpm",
    label: "CPM",
    group: "cost",
    format: "currency",
    needsCost: true,
    hint: "Spend per 1,000 impressions",
    compute: COST_PER("impressions", 1000),
  },
  {
    key: "cpe",
    label: "CPE",
    group: "cost",
    format: "currency",
    needsCost: true,
    hint: "Spend per engagement",
    compute: COST_PER("engagements"),
  },
  {
    key: "roas",
    label: "Return on spend",
    group: "cost",
    format: "multiple",
    needsCost: true,
    hint: "Tracked revenue ÷ spend",
    compute: (totals, { cost } = {}) => {
      if (!cost || !totals.revenue) return null;
      return totals.revenue / cost;
    },
  },
  {
    // The one cost metric that needs no setup: both halves come from the
    // ad_metrics rows Deliverable#statisticsValues already appends, and both
    // are already charted as donuts. Boosted spend only, never creator fees.
    key: "paid_cpm",
    label: "Paid media CPM",
    group: "cost",
    format: "currency",
    hint: "Ad spend ÷ 1,000 paid impressions",
    compute: (totals) => {
      if (!totals.ad_spend || !totals.paid_impressions) return null;
      return totals.ad_spend / (totals.paid_impressions / 1000);
    },
  },

  // --- Totals (already on the charts) ------------------------------------
  {
    key: "impressions",
    label: "Impressions",
    group: "totals",
    format: "number",
    compute: (t) => t.impressions,
  },
  {
    key: "impressions_unique",
    label: "Reach",
    group: "totals",
    format: "number",
    compute: (t) => t.impressions_unique,
  },
  {
    key: "views",
    label: "Views",
    group: "totals",
    format: "number",
    compute: (t) => t.views,
  },
  {
    key: "engagements",
    label: "Engagements",
    group: "totals",
    format: "number",
    compute: (t) => t.engagements,
  },
  {
    key: "outbound_clicks",
    label: "Link clicks",
    group: "totals",
    format: "number",
    compute: (t) => t.outbound_clicks,
  },
  {
    key: "revenue",
    label: "Revenue",
    group: "totals",
    format: "currency",
    compute: (t) => t.revenue,
  },
  {
    key: "post_count",
    label: "Posts",
    group: "totals",
    format: "number",
    compute: (t) => t.post_count,
  },
];

// Ashley Stevenson's ask (engagement rate per impression) plus the two
// averages that need nothing but impressions and engagements, so a freshly
// added section says something on every report rather than four dashes.
export const DEFAULT_METRICS = [
  "engagement_rate",
  "impressions_per_post",
  "engagements_per_post",
  "post_count",
];

const DEFINITIONS_BY_KEY = METRIC_DEFINITIONS.reduce((acc, def) => {
  acc[def.key] = def;
  return acc;
}, {});

export const findMetric = (key) => DEFINITIONS_BY_KEY[key] || null;

// The metric_types summed out of Deliverable#statisticsValues. `revenue` is
// already in dollars by the time it reaches the client — Deliverable
// #statisticsValues divides the stored cents by 100 — so it is summed as-is.
export const SUMMED_METRIC_TYPES = [
  "impressions",
  "impressions_unique",
  "views",
  "engagements",
  "outbound_clicks",
  "conversions",
  "revenue",
  "ad_spend",
  "paid_impressions",
];

// YouTube is deliberately NOT aggregated alongside the other channels: ReportCharts
// donuts `deliverablesExcludingYouTube` and prints "These metrics are not aggregated
// alongside other content channels" underneath, rendering YouTube's figures as their
// own stat cards instead.
//
// This section used to sum everything, so the same metric showed two values on one
// page — Views as 252,988 in a card and 251,653 in the donut directly below it,
// Engagements as 1,920 and 1,862. That is not a rounding difference, it is YouTube,
// and it made a genuinely broken report (one deliverable carrying a bad estimate)
// impossible to read: the two numbers disagreeing looked like the bug.
//
// One rule, defined here, consumed by both. Flip it in one place if the product call
// ever changes.
export const isAggregatable = (deliverable) =>
  deliverable && deliverable.inferred_type !== "youtube";

export const aggregatableDeliverables = (deliverables = []) =>
  deliverables.filter(isAggregatable);

export function totalsFor(allDeliverables = []) {
  const deliverables = aggregatableDeliverables(allDeliverables);
  const totals = SUMMED_METRIC_TYPES.reduce((acc, type) => {
    acc[type] = 0;
    return acc;
  }, {});
  // Counted off the SAME set the metrics are summed from. A post_count that
  // included the YouTube posts whose impressions were excluded would divide one
  // channel's numerator by every channel's denominator.
  totals.post_count = deliverables.length;
  totals.excluded_post_count = allDeliverables.length - deliverables.length;

  deliverables.forEach((deliverable) => {
    (deliverable.statisticsValues || []).forEach((stat) => {
      if (!(stat.type in totals) || !stat.total) return;
      totals[stat.type] += Number(stat.total.value) || 0;
    });
  });

  return totals;
}

export function computeMetric(key, totals, options = {}) {
  const def = findMetric(key);
  if (!def) return null;
  const value = def.compute(totals, options);
  return Number.isFinite(value) ? value : null;
}

// Same threshold ReportCharts passes for the number in the middle of each
// donut, so an "Impressions" card and the impressions donut it sits under
// print the identical string rather than 671,287 next to 671.3K.
// No numeric separator: this bundle's babel-preset-env (1.7.0) can't parse one.
const ABBREVIATE_ABOVE = 10000000;

export function formatCurrency(value) {
  if (value === null || value === undefined) return null;
  // Cost per engagement is routinely under a cent at scale, so cents always
  // survive: $0.42 and $6,398.33 both read exactly.
  if (Math.abs(value) >= ABBREVIATE_ABOVE) {
    return `$${abbreviateNumber(value, ABBREVIATE_ABOVE)}`;
  }
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

export function formatMetric(key, value) {
  if (value === null || value === undefined) return null;
  const def = findMetric(key);
  const format = def ? def.format : "number";

  switch (format) {
    case "percent":
      // Rates on this data land between ~0.1% and ~20%, so two decimals is
      // the difference between "1.24%" and a flat "1%".
      return `${value.toFixed(2)}%`;
    case "multiple":
      return `${value.toFixed(1)}×`;
    case "currency":
      return formatCurrency(value);
    default:
      // Per-post averages come out fractional (111,881.17 impressions per
      // post); nobody reads the decimal on a count.
      return abbreviateNumber(Math.round(value), ABBREVIATE_ABOVE);
  }
}
